import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../components/BreadCrumb";
import CustomButton from "../components/CustomButton";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AddNewArticle from "../components/modals/AddNewArticle";
import useFetch from "../hooks/useFetch";
import FadeMenu from "../components/FadeMenu";
import {
  deleteInitiative,
  deletePaper,
  deleteWorkStreamNote,
  getWorkStreamNotes,
} from "../api/Api";
import { InitiativeContext } from "../contexts/InitiativeContextProvider";
import { OrganizationContext } from "../contexts/OrganizationContextProvider";
import { useUser } from "../contexts/UserContextProvider";
import { showToast } from "../utils/toastUtils";
import DeleteModel from "../components/modals/DeleteModel";
import img from "../assets/emptybox.png";
import { PiUploadSimpleBold } from "react-icons/pi";
import RisksForWorkStream from "../components/RisksForWorkStream";
import AddNewRisk from "../components/modals/AddNewRisk";
import styles from "./Initiative.module.css";
import LessonsForWorkStream from "../components/LessonsForWorkStream";
import AddNewLesson from "../components/modals/AddNewLesson";
import Tabs from "../components/ui/Tabs";
import OverviewCard from "../components/ui/OverviewCard";
import { GeneralWorkStreamInfoCard } from "../components/ui/GeneralInfoCard";
import FileUpload from "../components/FileUpload";
import AddLinks from "../components/AddLinks";
import NoticeCard from "../components/ui/NoticeCard";
import Box from "../components/ui/Box";
import Linkify from "react-linkify";

// for notes
import { IoMdAddCircle } from "react-icons/io";
import { CgNotes } from "react-icons/cg";
import { IoIosArrowDown } from "react-icons/io";
import AddNote from "../components/modals/AddNote";
import PaperList from "../components/cards/PaperList";
import ActivityLog from "../components/ActivityLog";
import Tasks from "./Tasks";
import UpdateWorkstreamModal from "../components/modals/UpdateWorkstream";

export default function Initiative() {
  const navigate = useNavigate();
  const { data: organizationData } = useContext(OrganizationContext);
  const { userInfo } = useUser();
  const { initiativeId, projectId } = useParams();
  const [showAddArticle, setShowAddArticle] = useState(false);
  const [modalType, setModalType] = useState("Paper"); // paper or initiative
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("overview"); // overview, papers, risks, lessons this is the default tab
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectName = queryParams.get("name");
  const { data: project, refresh: refreshProjectInitiatives } =
    useContext(InitiativeContext);
  const [showAddRiskModal, setShowAddRiskModal] = useState(false); // Set State for AddNewRisk modal
  const [showAddLessonModal, setShowAddLessonModal] = useState(false); // Set State for AddNewLesson modal
  const [newRisks, setNewRisks] = useState([]); // State to store new risks
  const [newLessons, setNewLessons] = useState([]); // State to store new lessons
  const [showAddNotePopup, setShowAddNotePopup] = useState(false);
  const [notices, setNotices] = useState([]); // State to store workstream notes
  const [isEditNote, setIsEditNote] = useState(false);
  const [notice, setNotice] = useState({}); // State to store the note to edit
  const [showUpdateWorkstreamModal, setShowUpdateWorkstreamModal] =
    useState(false);

  const {
    data: initiative,
    error: initiativeError,
    loading: initiativeLoading,
    refresh,
  } = useFetch(
    `${process.env.REACT_APP_BACKEND_BASE_URL}/initiatives/${initiativeId}`
  );

  const organizationId = organizationData?.current_organization?.id;
  const workStreamId = initiativeId;
  const createdBy = userInfo?.id;

  const openAlert = () => setOpen(true); // for open initiative delete model
  const closeAlert = () => setOpen(false); // for close initiative delete model

  const handleNewArticle = () => setShowAddArticle(true);

  const handleMenuItemClick = async (item, value) => {
    if (item === "Delete WorkStream") {
      setModalType("Initiative");
      openAlert();
    }
    if (item === "Edit WorkStream") {
      setShowUpdateWorkstreamModal(true);
    }
  };

  const handleDeleteInitiative = (InitiativeId) => {
    deleteInitiative(InitiativeId)
      .then((response) => {
        if (response?.status === 200) {
          refreshProjectInitiatives();
          navigate(`/project/${projectId}`, { replace: true });
          showToast("Workstream deleted successfully", "success");
        } else showToast(response.message, "error");
      })
      .catch((error) => {
        showToast(
          error.response?.data?.message || "Error loading data",
          "error"
        );
      });
    closeAlert();
  };

  //handle function to add new lessons
  const handleAddNewLesson = (newLesson) => {
    setNewLessons((prevLessons) => [...prevLessons, newLesson]);
  };

  //handleAddRisk function to add a new risk to the newRisks state
  const handleAddNewRisk = (newRisk) => {
    setNewRisks((prevRisks) => [...prevRisks, newRisk]);
  };

  const handleShowRiskModal = () => {
    setShowAddRiskModal(true); // Only show AddRisk modal
  };

  const handleShowLessonModal = () => {
    setShowAddLessonModal(true); // Only show AddLesson modal
  };

  //handleCloseModal function to close the modals
  const handleCloseModal = () => {
    setShowAddRiskModal(false);
    setShowAddLessonModal(false);
  };

  // function to get workStream notes
  const fetchWorkStreamNotes = async (workStreamId) => {
    try {
      const response = await getWorkStreamNotes(workStreamId);
      if (response.status === 200) {
        setNotices(response.data);
      } else {
        // handle error
      }
    } catch (error) {
      // handle error
    }
  };

  // function to delete a note
  const handleDeleteNote = (noteId) => {
    deleteWorkStreamNote(workStreamId, noteId)
      .then((response) => {
        if (response?.status === 200) {
          showToast("Note deleted successfully", "success");
          fetchWorkStreamNotes(workStreamId);
        } else showToast(response.message, "error");
      })
      .catch((error) => {
        showToast(
          error.response?.data?.message || "Error loading data",
          "error"
        );
      });
  };

  // function to view a note (for editing)
  const handleViewNote = (notice) => {
    setNotice(notice);
    setIsEditNote(true); // Set to true for editing
    setShowAddNotePopup(true);
  };

  // function to add new note
  const handleAddNote = () => {
    setNotice({}); // Clear any existing note data
    setIsEditNote(false); // Set to false for adding new note
    setShowAddNotePopup(true);
  };

  // useEffect to get workstream notes
  useEffect(() => {
    fetchWorkStreamNotes(workStreamId);
  }, [workStreamId]);

  // array for breadcrumb
  const array = [
    { title: projectName, link: `/project/${projectId}` },
    { title: initiative?.name, link: "/" },
  ];

  // data for the overview card
  const cardData = {
    name: initiative?.name,
    description: initiative?.description,
    owner: initiative?.owner.first_name + " " + initiative?.owner.last_name,
    startDate: initiative?.start_date,
    endDate: initiative?.end_date,
    memberCount: initiative?.member_count,
    paperCount: initiative?.paper_count,
  };

  // list of tabs
  const list = ["overview", "goals", "risks", "lessons"];

  if (initiativeLoading) return <div>Loading...</div>;
  if (initiativeError) return <div>{initiativeError}</div>;

  return (
    <div className="d-flex gap-3 p-3 h-100">
      <div className={`rounded-4 overflow-hidden w-100`}>
        <>
          {/* BreadCrumb */}
          <BreadCrumb
            array={array}
            button={
              <>
                {tab === "overview" ? (
                  <FadeMenu
                    buttonLabel="..."
                    menuItems={["Edit WorkStream", "Delete WorkStream"]}
                    onMenuItemClick={handleMenuItemClick}
                    value={initiativeId}
                  />
                ) : (
                  <>
                    {tab === "papers" && (
                      <CustomButton
                        pre={<PiUploadSimpleBold sx={{ width: 24 }} />}
                        onClick={handleNewArticle}
                      >
                        Upload to validate
                      </CustomButton>
                    )}

                    {tab === "risks" && (
                      <CustomButton onClick={handleShowRiskModal}>
                        <AddRoundedIcon sx={{ width: 20 }} /> NEW RISK
                      </CustomButton>
                    )}
                    {tab === "lessons" && (
                      <CustomButton onClick={handleShowLessonModal}>
                        <AddRoundedIcon sx={{ width: 20 }} /> NEW LESSON
                      </CustomButton>
                    )}
                  </>
                )}
              </>
            }
          />

          {/* tabs overview, papers, risks, lessons */}
          <Tabs tab={tab} setTab={setTab} tabContentList={list} />
        </>

        <div className={`${styles.content} bg-white h-100`}>
          {tab === "overview" && (
            <Box>
              <OverviewCard
                cardData={{
                  ...cardData,
                  description: (
                    <span style={{ whiteSpace: "pre-line" }}>
                      {/*spaces and line breaks are preserved*/}
                      <Linkify>{cardData.description}</Linkify>{" "}
                      {/* urls are clickable */}
                    </span>
                  ),
                }}
              />
              <GeneralWorkStreamInfoCard cardData={cardData} className="mt-4" />
              {/*<FileUpload entityId={initiativeId} entityType="initiative" />*/}
              <AddLinks entityId={initiativeId} entityType="initiative" />
              <ActivityLog
                title={"Activity Log"}
                type="initiative"
                id={initiativeId}
              />
            </Box>
          )}

          <DeleteModel
            open={open && modalType === "Initiative"}
            closeAlert={closeAlert}
            onClick={() => handleDeleteInitiative(initiativeId)}
            type={modalType}
          />

          {/* goals tab */}
          {tab === "goals" && (
            <Tasks
              project={project}
              projectId={projectId}
              workStreamId={initiativeId}
            />
          )}

          {tab === "risks" && (
            <>
              <RisksForWorkStream
                organizationId={organizationId}
                workStreamId={workStreamId}
                createdBy={createdBy}
                handleAddRisk={handleAddNewRisk}
                newRisks={newRisks}
                showAddRiskModal={showAddRiskModal}
                setShowAddRiskModal={setShowAddRiskModal}
                handleCloseModal={handleCloseModal}
              />

              <AddNewRisk />
            </>
          )}

          {tab === "lessons" && (
            <>
              <LessonsForWorkStream
                organizationId={organizationId}
                workStreamId={workStreamId}
                createdBy={createdBy}
                handleAddLesson={handleAddNewLesson}
                newLessons={newLessons}
                showAddLessonModal={showAddLessonModal}
                setShowAddLessonModal={setShowAddLessonModal}
                handleCloseModal={handleCloseModal}
              />

              <AddNewLesson />
            </>
          )}
        </div>

        {/* Update Workstream Modal */}
        {showUpdateWorkstreamModal && (
          <UpdateWorkstreamModal
            show={showUpdateWorkstreamModal}
            handleClose={() => setShowUpdateWorkstreamModal(false)}
            initiative={initiative}
            projectId={projectId}
            refresh={refresh}
          />
        )}

        {/* add note popup */}
        <AddNote
          show={showAddNotePopup}
          workStream_id={workStreamId}
          entity_type="workStream"
          refresh={fetchWorkStreamNotes}
          handleClose={() => {
            setShowAddNotePopup(false);
            setNotice({});
          }}
          isEdit={isEditNote}
          setIsEdit={setIsEditNote}
          note={notice}
        />
      </div>

      {/* workstream notes */}
      {tab === "overview" && (
        <div
          style={{ maxWidth: "360px", minWidth: "300px" }}
          className="d-none d-xxl-block rounded-4 overflow-hidden"
        >
          <div className="d-flex justify-content-between align-items-center p-4 bg-body-secondary">
            <div className="d-flex align-items-center gap-2">
              <div className="rounded-2 border border-2 border-secondary-subtle">
                <IoIosArrowDown size={23} />
              </div>
              <CgNotes size={23} />
              <p className="p3 fw-medium text-center m-0">WorkStream Notes</p>
            </div>
            <IoMdAddCircle
              size={24}
              style={{ cursor: "pointer" }}
              onClick={handleAddNote} // Now properly handles adding new note
            />
          </div>
          <div className="d-flex flex-column gap-3 bg-white p-4">
            {notices.map((notice, index) => (
              <NoticeCard
                key={index}
                notice={notice}
                onClick={() => handleViewNote(notice)}
                onDelete={() => handleDeleteNote(notice.id)}
              />
            ))}
            {notices.length === 0 && (
              <div className="d-flex flex-column align-items-center justify-content-center gap-3 h-100">
                <p className="text-center text-color-tertiary fst-italic fs-6 mb-0">
                  No notes available.
                  <br /> You can add a new note by clicking on the '+' button
                  above.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
